// Captures the original Error out-of-band so server.ts can recover the stack
// when h3 has already swallowed the throw into a generic 500 Response.
//
// Also logs immediately with full stack + a "wallet/appkit" tag whenever the
// captured error's stack references wallet, wagmi, viem, or @reown/appkit —
// those are the modules that must never execute in the Cloudflare Worker
// global scope. This makes SSR global-scope leaks obvious in Server Logs
// even if the request path ends up in a generic 500.

let lastCapturedError: { error: unknown; at: number } | undefined;
const TTL_MS = 5_000;

const WALLET_STACK_PATTERNS = [
  /@reown\/appkit/i,
  /walletconnect/i,
  /\bwagmi\b/i,
  /\bviem\b/i,
  /src\/lib\/wallet(\.|-)/i,
  /WalletProvider/,
];

function stackMentionsWallet(stack: string | undefined): boolean {
  if (!stack) return false;
  return WALLET_STACK_PATTERNS.some((rx) => rx.test(stack));
}

function summarize(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: (error as { cause?: unknown }).cause,
    };
  }
  return { value: String(error) };
}

function record(error: unknown, source: "error" | "unhandledrejection") {
  lastCapturedError = { error, at: Date.now() };

  const stack = error instanceof Error ? error.stack : undefined;
  const message = error instanceof Error ? error.message : "";
  const walletRelated = stackMentionsWallet(stack) || stackMentionsWallet(message);
  const label = walletRelated ? "SSR wallet/appkit global-scope error" : `SSR global ${source}`;

  try {
    console.error(
      `[RobotDex ${label}] ${JSON.stringify({ source, walletRelated, error: summarize(error) })}`,
    );
  } catch {
    console.error(`[RobotDex ${label}]`, error);
  }
  // Also emit the raw Error so runtimes that resolve source maps in Server
  // Logs (Cloudflare, Node) print the full trace.
  if (error instanceof Error) console.error(error);
}

if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener("error", (event) => record((event as ErrorEvent).error ?? event, "error"));
  globalThis.addEventListener("unhandledrejection", (event) =>
    record((event as PromiseRejectionEvent).reason, "unhandledrejection"),
  );
}

export function consumeLastCapturedError(): unknown {
  if (!lastCapturedError) return undefined;
  if (Date.now() - lastCapturedError.at > TTL_MS) {
    lastCapturedError = undefined;
    return undefined;
  }
  const { error } = lastCapturedError;
  lastCapturedError = undefined;
  return error;
}

// Sentinel used by wallet-adjacent modules. If any of them accidentally
// evaluate on the server (global scope of the Cloudflare Worker), this
// prints a tagged, stack-carrying error to Server Logs. Purely diagnostic —
// does NOT throw, so the request can still proceed.
export function reportSsrModuleEval(moduleName: string, extra?: Record<string, unknown>) {
  if (typeof window !== "undefined") return; // client-side eval is expected
  const err = new Error(`SSR module evaluated in global scope: ${moduleName}`);
  try {
    console.error(
      `[RobotDex SSR module-eval] ${JSON.stringify({
        module: moduleName,
        extra: extra ?? null,
        stack: err.stack,
      })}`,
    );
  } catch {
    console.error(`[RobotDex SSR module-eval] ${moduleName}`, err, extra);
  }
  console.error(err);
}
